import React, { ReactNode, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import Button from 'components/common/button';
import HeaderLogo from 'components/common/headerLogo';
import IntakeStepList, {
  IntakeStep,
  IntakeStepKey,
} from 'components/common/intakeStepList';
import MetaTag from 'components/meta';
import { RootState } from 'store/store';
import { NullableAppointmentDetails, NullableCommunique } from 'types/types';
import { entryScreenContent } from 'templates/entryScreen';

type Props = {
  children: ReactNode;
  currentStep: IntakeStepKey;
  metaTitle: string;
  fullWidthContent?: boolean;
};

const stepOrder: IntakeStepKey[] = ['hpi', 'medicalHistory', 'pharmacy', 'forms'];

function IntakeStartScreen({
  children,
  currentStep,
  metaTitle,
  fullWidthContent = false,
}: Props) {
  const [isStarted, setIsStarted] = useState(false);
  const communiqueData = useSelector(
    (state: RootState) => state.communique,
  ) as NullableCommunique;
  const appointmentData = useSelector(
    (state: RootState) => state.appointment,
  ) as NullableAppointmentDetails;

  const steps = useMemo(() => {
    const appointmentCommunique = appointmentData?.appointment?.confirmAppointmentCommunique;
    const requestedForms = appointmentCommunique?.sendForms;
    const submissionStatus = appointmentCommunique?.intakeSubmissionStatus;
    const currentStepIndex = stepOrder.indexOf(currentStep);
    const isHpiEnabled = Boolean(
      requestedForms?.hpi
      || communiqueData?.sendForms?.hpi
      || currentStep === 'hpi',
    );
    const isMedicalHistoryEnabled = Boolean(
      requestedForms?.medicalHistory
      || communiqueData?.sendForms?.medicalHistory
      || currentStep === 'medicalHistory'
      || currentStep === 'pharmacy',
    );
    const hasForms = Boolean(
      communiqueData?.sendForms?.consentForm
      || communiqueData?.hasAgreementForms
      || communiqueData?.hasCCMConsentForms
      || communiqueData?.formWorkflowId
      || currentStep === 'forms',
    );
    const wasCompletedBeforeCurrentStep = (step: IntakeStepKey) => (
      stepOrder.indexOf(step) < currentStepIndex
    );

    return [
      isHpiEnabled && {
        key: 'hpi',
        label: communiqueData?.sendForms?.isFollowUp
          ? 'Update on your condition'
          : entryScreenContent.reason_for_visit,
        isComplete: submissionStatus?.hpiSubmitted
          ?? wasCompletedBeforeCurrentStep('hpi'),
      },
      isMedicalHistoryEnabled && {
        key: 'medicalHistory',
        label: entryScreenContent.medical_history,
        isComplete: submissionStatus?.medicalHistorySubmitted
          ?? wasCompletedBeforeCurrentStep('medicalHistory'),
      },
      isMedicalHistoryEnabled && {
        key: 'pharmacy',
        label: entryScreenContent.pharmacy_medications,
        isComplete: submissionStatus?.pharmacyAllergyMedicationsSubmitted
          ?? wasCompletedBeforeCurrentStep('pharmacy'),
      },
      hasForms && {
        key: 'forms',
        label: entryScreenContent.acknowledge_sign,
        subtext: entryScreenContent.acknowledge_sign_desc,
        isComplete: currentStep !== 'forms'
          && communiqueData?.sendForms?.consentForm !== true,
      },
    ].filter(Boolean) as IntakeStep[];
  }, [appointmentData, communiqueData, currentStep]);

  return (
    <div
      className={clsx(
        'app-page-container',
        { '!max-w-full !bg-transparent': isStarted && fullWidthContent },
      )}
    >
      {!isStarted ? (
        <div className="px-3">
          <MetaTag title={metaTitle} />
          <HeaderLogo />
          <div className="pt-2">
            <h2 className="mb-2 text-xl font-semibold leading-7 text-gray-800">
              {entryScreenContent.header}
            </h2>
            <p className="text-sm leading-6 text-[#77889C]">
              {entryScreenContent.header_desc}
            </p>
            <IntakeStepList steps={steps} currentStep={currentStep} />
          </div>
          <div className="next-button-fixed">
            <Button onClick={() => setIsStarted(true)}>
              Start
            </Button>
          </div>
        </div>
      ) : children}
    </div>
  );
}

export default IntakeStartScreen;
